import { useOrgTab } from '../../api/orgs';
import type { OrgConnections } from '../../api/types';
import { DataTable, type DataColumn } from '../../components/DataTable';
import { QueryView } from '../../components/QueryView';
import { StatusBadge } from '../../components/StatusBadge';
import { Timestamp } from '../../components/Timestamp';

type Connection = OrgConnections['connections'][number];

const NONE = <span className="sk:text-fg-tertiary">None</span>;

const COLUMNS: DataColumn<Connection>[] = [
  {
    id: 'provider',
    header: 'Provider',
    cell: connection => connection.provider,
  },
  {
    id: 'status',
    header: 'Status',
    cell: connection => <StatusBadge status={connection.status} />,
  },
  {
    id: 'stream',
    header: 'Stream state',
    cell: connection =>
      connection.stream_states.length === 0
        ? NONE
        : connection.stream_states.map(stream => (
            <div key={stream.stream}>
              {stream.stream}: {stream.state}
            </div>
          )),
  },
  {
    id: 'alerts',
    header: 'Open alerts',
    cell: connection =>
      connection.open_alerts.length === 0
        ? NONE
        : connection.open_alerts.map(alert => (
            <div key={alert.id}>
              {alert.kind}
              {alert.message ? `: ${alert.message}` : ''}{' '}
              <Timestamp value={alert.opened_at} />
            </div>
          )),
  },
  {
    id: 'health',
    header: 'Last health events',
    cell: connection =>
      connection.last_health_events.length === 0
        ? NONE
        : connection.last_health_events.map(event => (
            <div key={`${event.at}-${event.kind}`}>
              {event.kind}
              {event.message ? `: ${event.message}` : ''}{' '}
              <Timestamp value={event.at} />
            </div>
          )),
  },
];

export function ConnectionsTab({ orgId }: { orgId: string }) {
  return (
    <QueryView query={useOrgTab(orgId, 'connections')}>
      {({ connections }) => (
        <DataTable
          label="Connections"
          columns={COLUMNS}
          rows={connections}
          getKey={connection => connection.id}
          empty="No connections"
        />
      )}
    </QueryView>
  );
}
